/* @HEADER
 * @version 1.1.963 | 2026-10-26
 * @purpose Keeps the person in their space when the device replaces the local row of that space by another row of the SAME server space (ZVX-DEV-8355, ZVX-4685): the space on screen follows its successor instead of being left pointing at a row that is gone.
 * @sidecar useActiveSpaceFollowsReplacement.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8355, ZVX-DEV-3274

import { useEffect, useRef } from 'react'

interface SpaceRow {
  id: string
  serverSpaceId?: string | null
}

export interface ActiveSpaceFollowsReplacementArgs {
  groups: readonly SpaceRow[]
  /** The groups are being read: a list being read is not a replacement. */
  loading: boolean
  activeSpaceId: string | null
  setActiveSpaceId: (id: string | null) => void
}

/**
 * The reconcile of the project's spaces (`reconcileProjectSpaces`, ZVX-DEV-3274) merges two rows of one server space (a `bg-<ms>` from the
 * catalog and a `space-server-<uuid>` from the registry) and DROPS one; its boards are re-hung on the survivor. The space on screen is held by
 * the local id of the row it was taken into, so when that row is the dropped one it points at nothing, and the page names itself by the built-in
 * default («Входящие») and shows no boards. Measured by e2e-1 on a link into another project: the title changed 1.5-2 s after the landing.
 *
 * So: remember the last row seen for the space on screen (its id and its server id); when that row is gone and another row carries the same
 * server id, move to it. No successor, no server id, a space never seen, a list still loading, or a person who moved by themselves: nothing.
 */
export function useActiveSpaceFollowsReplacement({ groups, loading, activeSpaceId, setActiveSpaceId }: ActiveSpaceFollowsReplacementArgs): void {
  const lastSeen = useRef<{ id: string; serverSpaceId: string | null } | null>(null)
  const followedFrom = useRef<string | null>(null)

  useEffect(() => {
    if (loading) return
    if (!activeSpaceId) {
      lastSeen.current = null
      return
    }
    const here = groups.find((g) => g.id === activeSpaceId)
    if (here) {
      lastSeen.current = { id: here.id, serverSpaceId: here.serverSpaceId ?? null }
      followedFrom.current = null
      return
    }
    const last = lastSeen.current
    if (!last || last.id !== activeSpaceId || !last.serverSpaceId) return
    if (followedFrom.current === activeSpaceId) return
    const successor = groups.find((g) => g.id !== activeSpaceId && g.serverSpaceId === last.serverSpaceId)
    if (!successor) return
    followedFrom.current = activeSpaceId
    // Debug level only, and only the step: no id (ZVX-DEV-8351).
    console.debug('[nav-link]', 'space-followed')
    setActiveSpaceId(successor.id)
  }, [groups, loading, activeSpaceId, setActiveSpaceId])
}
